// INN-5: Style Consistency Checker — scans page components for design violations
// Detects font sprawl, color sprawl, inconsistent spacing/radius/heading sizes
import { useCallback, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle, ChevronDown, Wrench } from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type IssueSeverity = "error" | "warning" | "info";
type IssueCategory = "fonts" | "font-sizes" | "colors" | "spacing" | "border-radius";

interface ConsistencyIssue {
  readonly id: string;
  readonly category: IssueCategory;
  readonly severity: IssueSeverity;
  readonly description: string;
  readonly affectedComponents: readonly string[];
  readonly fixAction: FixAction | null;
}

interface FixAction {
  readonly label: string;
  readonly componentFixes: ReadonlyArray<{
    readonly componentId: string;
    readonly prop: string;
    readonly value: unknown;
  }>;
}

interface ConsistencyCheckerProps {
  components: ReadonlyArray<{
    id: string;
    type: string;
    props: Record<string, unknown>;
  }>;
  onFix: (fixes: FixAction["componentFixes"]) => void;
  onSelectComponent?: (componentId: string) => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_UNIQUE_FONTS = 2;
const MAX_UNIQUE_NONTOKENCOLORS = 5;
const MAX_UNIQUE_RADII = 3;

// CSS variable pattern — values referencing tokens are considered "on-system"
const CSS_VAR_RE = /^var\(--/;

// Common color formats
const COLOR_RE = /^(#[0-9a-fA-F]{3,8}|rgba?\(|hsla?\()/;

// Token color references we consider "on-system"
const TOKEN_COLOR_PREFIXES = [
  "var(--color-",
  "var(--gradient-",
] as const;

const CATEGORY_LABELS: Record<IssueCategory, string> = {
  fonts: "Font Families",
  "font-sizes": "Font Sizes",
  colors: "Colors",
  spacing: "Spacing",
  "border-radius": "Border Radius",
};

// ---------------------------------------------------------------------------
// Analysis engine
// ---------------------------------------------------------------------------

function isTokenColor(value: string): boolean {
  return TOKEN_COLOR_PREFIXES.some((prefix) => value.startsWith(prefix));
}

function extractStringProps(
  props: Record<string, unknown>,
  keys: readonly string[],
): string[] {
  const values: string[] = [];
  for (const key of keys) {
    const val = props[key];
    if (typeof val === "string" && val.trim().length > 0) {
      values.push(val.trim());
    }
  }
  return values;
}

function mostCommon(values: string[]): string | undefined {
  const counts = new Map<string, number>();
  for (const v of values) {
    counts.set(v, (counts.get(v) ?? 0) + 1);
  }
  let best: string | undefined;
  let bestCount = 0;
  for (const [v, c] of counts) {
    if (c > bestCount) {
      bestCount = c;
      best = v;
    }
  }
  return best;
}

function analyzeComponents(
  components: ConsistencyCheckerProps["components"],
): ConsistencyIssue[] {
  const issues: ConsistencyIssue[] = [];
  let issueIdx = 0;

  // --- Font families ---
  const fontMap = new Map<string, string[]>(); // font → componentIds
  for (const comp of components) {
    for (const val of extractStringProps(comp.props, ["fontFamily", "font-family"])) {
      if (CSS_VAR_RE.test(val)) continue;
      const existing = fontMap.get(val) ?? [];
      existing.push(comp.id);
      fontMap.set(val, existing);
    }
  }
  if (fontMap.size > MAX_UNIQUE_FONTS) {
    const dominant = mostCommon([...fontMap].flatMap(([font, ids]) => ids.map(() => font)));
    issues.push({
      id: `issue-${issueIdx++}`,
      category: "fonts",
      severity: "warning",
      description: `${fontMap.size} different font families used: ${[...fontMap.keys()].join(", ")} -- consider using ${MAX_UNIQUE_FONTS} max`,
      affectedComponents: [...fontMap.values()].flat(),
      fixAction: dominant
        ? {
            label: `Standardize to "${dominant}"`,
            componentFixes: [...fontMap]
              .filter(([font]) => font !== dominant)
              .flatMap(([, ids]) => ids.map((id) => ({ componentId: id, prop: "fontFamily", value: dominant }))),
          }
        : null,
    });
  }

  // --- Font sizes (headings should descend) ---
  const headingSizes: Array<{ componentId: string; level: number; size: string; numericPx: number }> = [];
  for (const comp of components) {
    const level = parseHeadingLevel(comp.type, comp.props);
    if (level === null) continue;
    const size = (comp.props["fontSize"] ?? comp.props["font-size"]) as string | undefined;
    if (!size) continue;
    const px = parsePx(size);
    if (px === null) continue;
    headingSizes.push({ componentId: comp.id, level, size, numericPx: px });
  }
  headingSizes.sort((a, b) => a.level - b.level);
  for (let i = 1; i < headingSizes.length; i++) {
    const prev = headingSizes[i - 1]!;
    const curr = headingSizes[i]!;
    if (curr.level > prev.level && curr.numericPx >= prev.numericPx) {
      issues.push({
        id: `issue-${issueIdx++}`,
        category: "font-sizes",
        severity: "warning",
        description: `H${prev.level} (${prev.size}) is not larger than H${curr.level} (${curr.size}) -- heading sizes should descend`,
        affectedComponents: [prev.componentId, curr.componentId],
        fixAction: {
          label: `Set H${curr.level} to ${Math.round(prev.numericPx * 0.8)}px`,
          componentFixes: [{ componentId: curr.componentId, prop: "fontSize", value: `${Math.round(prev.numericPx * 0.8)}px` }],
        },
      });
    }
  }

  // --- Colors (non-token) ---
  const nonTokenColors = new Map<string, string[]>();
  for (const comp of components) {
    for (const val of extractStringProps(comp.props, ["color", "backgroundColor", "background-color", "borderColor", "border-color"])) {
      if (isTokenColor(val) || CSS_VAR_RE.test(val)) continue;
      if (!COLOR_RE.test(val)) continue;
      const normalized = val.toLowerCase();
      const existing = nonTokenColors.get(normalized) ?? [];
      existing.push(comp.id);
      nonTokenColors.set(normalized, existing);
    }
  }
  if (nonTokenColors.size > MAX_UNIQUE_NONTOKENCOLORS) {
    issues.push({
      id: `issue-${issueIdx++}`,
      category: "colors",
      severity: "warning",
      description: `${nonTokenColors.size} unique non-token colors found -- consider using design token CSS variables (max ${MAX_UNIQUE_NONTOKENCOLORS} custom colors)`,
      affectedComponents: [...new Set([...nonTokenColors.values()].flat())],
      fixAction: null,
    });
  }

  // --- Spacing inconsistencies ---
  const spacingValues = new Map<string, string[]>();
  for (const comp of components) {
    for (const val of extractStringProps(comp.props, ["padding", "margin", "gap"])) {
      if (CSS_VAR_RE.test(val)) continue;
      const existing = spacingValues.get(val) ?? [];
      existing.push(comp.id);
      spacingValues.set(val, existing);
    }
  }
  const singleUseSpacing = [...spacingValues].filter(([, ids]) => ids.length === 1);
  if (singleUseSpacing.length > 3 && spacingValues.size > 5) {
    const dominant = mostCommon([...spacingValues].flatMap(([v, ids]) => ids.map(() => v)));
    issues.push({
      id: `issue-${issueIdx++}`,
      category: "spacing",
      severity: "info",
      description: `${spacingValues.size} unique spacing values with ${singleUseSpacing.length} one-off values -- consider standardizing to spacing tokens`,
      affectedComponents: singleUseSpacing.map(([, ids]) => ids[0]!),
      fixAction: dominant
        ? {
            label: `Standardize one-off spacing to "${dominant}"`,
            componentFixes: singleUseSpacing.map(([, ids]) => ({
              componentId: ids[0]!,
              prop: "padding",
              value: dominant,
            })),
          }
        : null,
    });
  }

  // --- Border radius ---
  const radiusValues = new Map<string, string[]>();
  for (const comp of components) {
    for (const val of extractStringProps(comp.props, ["borderRadius", "border-radius"])) {
      if (CSS_VAR_RE.test(val)) continue;
      const existing = radiusValues.get(val) ?? [];
      existing.push(comp.id);
      radiusValues.set(val, existing);
    }
  }
  if (radiusValues.size > MAX_UNIQUE_RADII) {
    const dominant = mostCommon([...radiusValues].flatMap(([v, ids]) => ids.map(() => v)));
    issues.push({
      id: `issue-${issueIdx++}`,
      category: "border-radius",
      severity: "warning",
      description: `${radiusValues.size} unique border-radius values: ${[...radiusValues.keys()].join(", ")} -- consider using ${MAX_UNIQUE_RADII} max`,
      affectedComponents: [...new Set([...radiusValues.values()].flat())],
      fixAction: dominant
        ? {
            label: `Standardize to "${dominant}"`,
            componentFixes: [...radiusValues]
              .filter(([v]) => v !== dominant)
              .flatMap(([, ids]) => ids.map((id) => ({ componentId: id, prop: "borderRadius", value: dominant }))),
          }
        : null,
    });
  }

  return issues;
}

// ---------------------------------------------------------------------------
// Utility parsers
// ---------------------------------------------------------------------------

function parseHeadingLevel(type: string, props: Record<string, unknown>): number | null {
  // Support "Heading", "heading", "h1"..."h6", or props.headingLevel
  const lower = type.toLowerCase();
  const match = lower.match(/^h(\d)$/);
  if (match) return parseInt(match[1] ?? "0", 10);
  if (lower.includes("heading")) {
    const level = props["level"] ?? props["headingLevel"];
    if (typeof level === "number" && level >= 1 && level <= 6) return level;
  }
  return null;
}

function parsePx(value: string): number | null {
  if (value.endsWith("px")) {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : null;
  }
  if (value.endsWith("rem")) {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n * 16 : null;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ConsistencyChecker({ components, onFix, onSelectComponent }: ConsistencyCheckerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<IssueCategory>>(new Set());

  const issues = useMemo(() => analyzeComponents(components), [components]);

  const issuesByCategory = useMemo(() => {
    const map = new Map<IssueCategory, ConsistencyIssue[]>();
    for (const issue of issues) {
      const arr = map.get(issue.category) ?? [];
      arr.push(issue);
      map.set(issue.category, arr);
    }
    return map;
  }, [issues]);

  const toggleCategory = useCallback((cat: IssueCategory) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  }, []);

  const handleFix = useCallback(
    (action: FixAction) => {
      onFix(action.componentFixes);
    },
    [onFix],
  );

  const isClean = issues.length === 0;

  return (
    <div className="relative">
      {/* Badge trigger */}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border transition-colors"
        style={{
          borderColor: isClean ? "var(--color-success)" : "var(--color-warning)",
          color: isClean ? "var(--color-success)" : "var(--color-warning)",
          backgroundColor: isClean ? "transparent" : "var(--color-accent-action-light)",
          transitionDuration: "var(--duration-fast)",
        }}
        aria-expanded={isOpen}
      >
        {isClean ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}
        <span className="text-[var(--text-sm)] font-medium">
          Check Consistency
        </span>
        <span className="text-[10px] opacity-80">
          {isClean ? "Clean" : `${issues.length} issue${issues.length > 1 ? "s" : ""}`}
        </span>
      </button>

      {/* Panel */}
      {isOpen && (
        <div
          className="absolute right-0 top-full mt-2 w-[380px] max-h-[480px] overflow-y-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] z-50"
          style={{ boxShadow: "var(--shadow-lg)" }}
        >
          <div className="px-4 py-3 border-b border-[var(--color-border)]">
            <span className="text-[var(--text-sm)] font-semibold text-[var(--color-foreground)]">
              Style Consistency
            </span>
            <span className="ml-2 text-[var(--text-xs)] text-[var(--color-muted-foreground)]">
              {components.length} components scanned
            </span>
          </div>

          {isClean ? (
            <div className="flex flex-col items-center gap-2 py-8 px-4 text-center">
              <CheckCircle size={32} className="text-[var(--color-success)]" />
              <p className="text-[var(--text-sm)] text-[var(--color-foreground)] font-medium">
                All clear
              </p>
              <p className="text-[var(--text-xs)] text-[var(--color-muted-foreground)]">
                No style inconsistencies detected
              </p>
            </div>
          ) : (
            <div>
              {[...issuesByCategory].map(([category, categoryIssues]) => {
                const isExpanded = expandedCategories.has(category);
                return (
                  <div key={category} className="border-b border-[var(--color-border)] last:border-b-0">
                    <button
                      type="button"
                      onClick={() => toggleCategory(category)}
                      className="flex items-center justify-between w-full px-4 py-2.5 text-left hover:bg-[var(--color-surface)] transition-colors"
                      style={{ transitionDuration: "var(--duration-fast)" }}
                      aria-expanded={isExpanded}
                    >
                      <span className="text-[var(--text-sm)] font-medium text-[var(--color-foreground)]">
                        {CATEGORY_LABELS[category]}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[var(--color-warning)] text-white font-medium">
                          {categoryIssues.length}
                        </span>
                        <ChevronDown
                          size={14}
                          className="text-[var(--color-muted-foreground)] transition-transform"
                          style={{
                            transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                            transitionDuration: "var(--duration-fast)",
                          }}
                        />
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="px-4 pb-3">
                        {categoryIssues.map((issue) => (
                          <div
                            key={issue.id}
                            className="mb-2 last:mb-0 p-3 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)]"
                          >
                            <p className="text-[var(--text-xs)] text-[var(--color-foreground)] leading-relaxed">
                              {issue.description}
                            </p>
                            <p className="mt-1 text-[10px] text-[var(--color-muted-foreground)]">
                              Affects: {issue.affectedComponents.length} component{issue.affectedComponents.length > 1 ? "s" : ""}
                            </p>
                            {issue.affectedComponents.length > 0 ? (
                              <div className="mt-2 flex flex-wrap gap-1">
                                {issue.affectedComponents.map((componentId) => (
                                  <button
                                    key={`${issue.id}-${componentId}`}
                                    type="button"
                                    onClick={() => onSelectComponent?.(componentId)}
                                    disabled={!onSelectComponent}
                                    className="rounded border border-[var(--color-border)] px-1.5 py-0.5 text-[10px] text-[var(--color-muted-foreground)] disabled:cursor-default disabled:opacity-80"
                                  >
                                    {componentId}
                                  </button>
                                ))}
                              </div>
                            ) : null}
                            {issue.fixAction?.componentFixes.length ? (
                              <div className="mt-2 space-y-1 rounded border border-[var(--color-border)] bg-[var(--color-card)] p-2">
                                {issue.fixAction.componentFixes.map((fix) => (
                                  <button
                                    key={`${issue.id}-${fix.componentId}-${fix.prop}`}
                                    type="button"
                                    onClick={() => onSelectComponent?.(fix.componentId)}
                                    disabled={!onSelectComponent}
                                    className="grid w-full grid-cols-[1fr,auto] gap-2 text-[10px] text-[var(--color-muted-foreground)] disabled:cursor-default"
                                  >
                                    <span className="truncate">
                                      {fix.componentId} · {fix.prop}
                                    </span>
                                    <span className="truncate text-right text-[var(--color-foreground)]">
                                      {String(fix.value)}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            ) : null}
                            {issue.fixAction && (
                              <button
                                type="button"
                                onClick={() => handleFix(issue.fixAction!)}
                                className="mt-2 flex items-center gap-1 px-2 py-1 rounded text-[var(--text-xs)] font-medium bg-[var(--color-primary)] text-[var(--color-primary-foreground)] hover:opacity-90 transition-opacity"
                                style={{ transitionDuration: "var(--duration-fast)" }}
                              >
                                <Wrench size={12} />
                                {issue.fixAction.label}
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

